import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { projectService } from '../services/api';
import { Loader2, Trash2, Upload } from 'lucide-react';

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [uploadStatus, setUploadStatus] = useState({});
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await projectService.getProjects(token);
      setProjects(data.projects || []);
    } catch (err) {
      setError('Error loading projects');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    try {
      await projectService.createProject(newProjectName, token);
      setNewProjectName('');
      fetchProjects();
    } catch (err) {
      setError('Error creating project');
      console.error('Error:', err);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectService.deleteProject(projectId, token);
      await fetchProjects();
    } catch (err) {
      setError('Error deleting project');
      console.error('Error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-6">Projects</h1>
        
        <form onSubmit={handleCreateProject} className="flex gap-4 mb-6">
          <input
            type="text"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            placeholder="Enter project name"
            className="flex-1 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={!newProjectName.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            Create Project
          </button>
        </form>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.project_id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <Link 
                to={`/projects/${project.project_id}`}
                className="text-xl font-semibold text-blue-600 hover:text-blue-800"
              >
                {project.project_name}
              </Link>
              <button
                onClick={() => handleDeleteProject(project.project_id)}
                className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors"
                title="Delete project"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
            
            {project.description && (
              <p className="text-gray-600 mb-4">{project.description}</p>
            )}
            
            <p className="text-sm text-gray-500 mb-4">
              Created: {new Date(project.date_created).toLocaleDateString()}
            </p>
          </div>
        ))}
      </div>

      {projects.length === 0 && !loading && (
        <div className="text-center text-gray-500 mt-8">
          No projects found. Create your first project above!
        </div>
      )}
    </div>
  );
}

export default Projects;